import { useEffect, useMemo, useRef, useState } from "react";
import type { LearnosityInitOptions } from "../types";
import { isGameLadderType } from "../utils/ladder-util";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    LearnosityItems: any;
  }
}

interface LearnosityEvents {
  "test:start"?: (...args: unknown[]) => void;
  "test:resume"?: (...args: unknown[]) => void;
  "test:finished:submit"?: (...args: unknown[]) => void;
  [key: string]: ((...args: unknown[]) => void) | undefined;
}

interface UseLearnosityPlayerOptions {
  learnosityInitOptions: LearnosityInitOptions | null;
  sessionId: string | null;
  events: LearnosityEvents;
  ladderType: string;
}

interface UseLearnosityPlayerReturn {
  divId: string;
  loading: boolean;
  error: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemsApi: any;
}

const stripNonIdChars = (str: string) => str.replace(/[^a-zA-Z0-9]/g, "");

export const useLearnosityPlayer = ({
  learnosityInitOptions,
  sessionId,
  events,
  ladderType,
}: UseLearnosityPlayerOptions): UseLearnosityPlayerReturn => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [itemsApi, setItemsApi] = useState<any>(null);
  const [error, setError] = useState(false);
  const eventsRef = useRef(events);
  eventsRef.current = events;

  const divId = useMemo(
    () => `learnosity-${stripNonIdChars(sessionId || "")}-${Date.now()}`,
    [sessionId],
  );

  const shouldHideDoneButton = isGameLadderType(ladderType);

  useEffect(() => {
    if (!learnosityInitOptions || !sessionId || !window.LearnosityItems) {
      return;
    }

    // Don't re-init if already initialized for this session
    if (itemsApi?.forSessionId === sessionId) {
      return;
    }

    try {
      const itemsApiInstance = window.LearnosityItems.init(learnosityInitOptions, `#${divId}`, {
        readyListener: () => {
          console.info("Learnosity activity is ready");
        },
        errorListener: () => {
          setError(true);
        },
      });

      itemsApiInstance.forSessionId = sessionId;
      setItemsApi(itemsApiInstance);

      // Register event handlers
      Object.keys(eventsRef.current).forEach((key) => {
        const handler = eventsRef.current[key];
        if (handler) {
          itemsApiInstance.on(key, (...args: unknown[]) => handler(itemsApiInstance, ...args));
        }
      });

      // Hide done button for game ladders
      if (shouldHideDoneButton) {
        itemsApiInstance.on("test:start", () => {
          const doneButton = document.querySelector("#lrn_assess_next_btn") as HTMLElement | null;
          if (doneButton) {
            doneButton.style.opacity = "0";
            doneButton.style.pointerEvents = "none";

            Object.keys(itemsApiInstance.getQuestions()).forEach((qid: string) => {
              itemsApiInstance.question(qid).on("changed", () => {
                doneButton.style.opacity = "1";
                doneButton.style.pointerEvents = "auto";
              });
            });
          }
        });
      }
    } catch (e) {
      console.error("Error initializing Learnosity", e);
      setError(true);
    }
  }, [learnosityInitOptions, sessionId, divId, shouldHideDoneButton]);

  const loading = !itemsApi && !!sessionId && !!learnosityInitOptions && !error;

  return {
    divId,
    loading,
    error,
    itemsApi,
  };
};
